<template>
  <div
    class="min-h-full text-n-800 font-body text-sm"
    :class="{
      'bg-n-0': isInIframe,
      'bg-n-10': !isInIframe
    }"
    :style="cssVariables"
  >
    <div v-if="isInIframe" class="p-4 max-w-[29rem] m-auto">
      <router-view></router-view>
    </div>
    <div
      v-else
      class="flex flex-col w-full items-center px-4 gap-4 pt-20 pb-14 lg:pt-40 lg:flex-row lg:items-start lg:justify-center lg:gap-16 bg-no-repeat bg-contain relative"
    >
      <div
        class="fixed inset-0 z-0 bg-cover bg-no-repeat bg-center h-32 lg:h-[27rem] w-full"
        :style="{ backgroundImage: `url(${coverImage})` }"
      ></div>
      <div
        class="z-10 bg-n-0 px-4 py-6 lg:py-10 flex flex-col gap-4 lg:gap-6 rounded-lg overflow-hidden w-full max-w-[29rem]"
      >
        <div class="flex" v-if="logoImage">
          <img :src="logoImage" class="h-8 lg:h-auto" />
        </div>
        <div class="p-4 flex flex-col gap-4 bg-n-10 rounded-lg">
          <div class="font-heading font-bold text-lg lg:text-xl w-full">
            {{ locationName }}
          </div>
          <div
            class="flex flex-col gap-2 text-n-300 font-body text-xs leading-4 lg:leading-5 lg:text-sm"
          >
            <div v-if="!!locationAddress" class="flex items-start gap-1 lg:gap-2">
              <l-icon name="location" />
              {{ locationAddress }}
            </div>
            <div v-if="!!locationPhoneNumber" class="flex items-start gap-1 lg:gap-2">
              <l-icon name="phone" />
              {{ locationPhoneNumber }}
            </div>
          </div>
        </div>
        <div v-if="extraText" class="font-body text-sm text-n-300">
          {{ extraText }}
        </div>
        <div>
          <a
            href="https://www.last.app?utm_medium=organic&utm_source=last.bookings"
            target="_blank"
            rel="nofollow"
          >
            <img src="/powered-by.png" class="flex h-4" />
          </a>
        </div>
      </div>

      <div
        class="z-10 bg-n-0 rounded-lg border border-n-50 overflow-hidden p-4 w-full max-w-[29rem]"
      >
        <router-view></router-view>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue'
import type { Locale } from 'date-fns'
import { setDefaultOptions } from 'date-fns'
import { ca, de, enGB, es } from 'date-fns/locale'
import { storeToRefs } from 'pinia'
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

import { LIcon } from '@last/core-ui/paprika'

import { useConfigStore } from '@/store/config'

const configStore = useConfigStore()
const { locale } = useI18n()

const { brandInfo, locationAddress, locationPhoneNumber, extraText } = storeToRefs(configStore)

function chooseLanguage() {
  const validLanguages = ['es', 'en', 'de', 'ca']
  const userLanguage = window.navigator.language.split('-')[0]
  locale.value = validLanguages.includes(userLanguage) ? userLanguage : 'en'

  const locales: Record<string, Locale> = {
    es: es,
    ca: ca,
    de: de,
    en: enGB
  }
  setDefaultOptions({
    locale: locales[locale.value]
  })
}

onMounted(() => {
  chooseLanguage()
})

const brandName = computed(() => brandInfo.value?.name || 'LastBookings')

const faviconUrl = computed(() => {
  const faviconImageId = brandInfo.value?.faviconImageId
  if (!faviconImageId) return null
  return `https://res.cloudinary.com/lastpos/image/upload/h_32,w_32,f_auto,q_auto/${faviconImageId}`
})

const cssVariables = computed(() => ({
  '--primary-color': brandInfo.value?.accentColor || '#3ED3E0',
  '--secondary-color': brandInfo.value?.secondaryColor || '#545383'
}))

const coverImage = computed(() => {
  if (!brandInfo.value?.coverImageId) return null
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
  const width = isMobile ? 412 : 1920
  const height = isMobile ? 128 : 440
  return `https://res.cloudinary.com/lastpos/image/upload/f_auto,q_auto/h_${height * 2},w_${width * 2},c_fill/${brandInfo.value.coverImageId}`
})

const logoImage = computed(() => {
  if (inIframe.value) return null
  if (!brandInfo.value?.colorLogoImageId) return null
  return `https://res.cloudinary.com/lastpos/image/upload/f_auto,q_auto/h_50,w_150,c_fit/${brandInfo.value.colorLogoImageId}`
})

const inIframe = computed(() => {
  try {
    return window.self !== window.top
  } catch (_) {
    return true
  }
})

const locationName = computed(() => brandInfo.value?.name)

const isInIframe = computed(() => {
  try {
    return window.self !== window.top
  } catch (_) {
    return true
  }
})

useHead({
  title: () => brandName.value,
  link: () =>
    faviconUrl.value
      ? [
          {
            rel: 'icon',
            href: faviconUrl.value
          }
        ]
      : []
})
</script>
